import type { DraftTaskAttachmentList } from '@frontend/api-client';
import { FILE_INPUT_COUNT_MAX_FILES, getScopeFailureExplanationCopy } from '@frontend/api-client';
import { CreateDraftTaskProvider, useCreateDraftTask, useDescriptionStep } from '@frontend/react-domain';
import {
  Button,
  Divider,
  DragAndDropModal,
  FileInput,
  FilePreviewsList,
  Icon,
  InlineAlert,
  Spinner,
  TextareaInput,
  XStack,
  YStack,
} from '@frontend/web-react';
import { applyClass, concatClasses, getHotkeyHandler, safeCss } from '@frontend/web-utils';
import { WebTypedFormData } from '@frontend/web-utils/webTypedFormData';
import { GradientBorderCard } from '@frontend/web/components/DomainSpecific/Tasks';
import {
  useAnalytics,
  useCreateDraftTaskMutation,
  useEditDraftTaskAfterFailureMutation,
  useLazyRetrieveDraftTaskQuery,
  useUpdateDraftTaskMutation,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

const HomeCreateTaskInputInner = () => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const { isRetrieveDraftTaskLoading, manualRefetchScopeCheck, showScopeCheckStuckButton, state } =
    useCreateDraftTask();

  const {
    handleDescriptionChange,
    handleFileChoose,
    handleFileRemove,
    isAnyLoading,
    isContinueDisabled,
    onPressContinue,
  } = useDescriptionStep({
    TypedFormData: WebTypedFormData,
    handleMutation,
    incrementParentStep: () => undefined,
    onCreateDraftSuccess: (draftTaskId) => {
      track({ data: { entryPoint: 'home' }, event: 'draftTaskCreated', topic: 'draftTasks' });
      navigate({ params: { draftTaskId }, replace: false, search: { source: 'home' }, to: '/app/draft-task/$draftTaskId' });
    },
    platformSource: 'task_source_web_app',
    useCreateDraftTaskMutation,
    useEditDraftTaskAfterFailureMutation,
    useUpdateDraftTaskMutation,
  });

  const handleStartTaskCreation = () => {
    track({ data: { entryPoint: 'home_input' }, event: 'taskCreationStarted', topic: 'draftTasks' });
    void onPressContinue();
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    // eslint-disable-next-line fp/no-let
    let singleLineHeight: number | null = null;
    const observer = new ResizeObserver(() => {
      if (singleLineHeight === null) {
        // eslint-disable-next-line fp/no-mutation
        singleLineHeight = el.offsetHeight;
        return;
      }
      setIsMultiLine(el.offsetHeight > singleLineHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAnyLoading || !state.description) return;
    handleStartTaskCreation();
  };

  return (
    <YStack className="w-full">
      <GradientBorderCard>
        <form onSubmit={handleSubmit}>
          <XStack
            // eslint-disable-next-line tailwindcss/no-custom-classname
            className="bg-white px-lg py-lg gap-sm hover:cursor-text w-full"
            onClick={() => textareaRef.current?.focus()}
          >
          <YStack className="gap-sm grow-1">
            <TextareaInput
              autoFocus
              classNames={{
                input: safeCss('text-h3 font-normal border-none p-none bg-transparent'),
              }}
              disabled={state.isScopeCheckLoading}
              maxRows={4}
              minRows={1}
              onChange={handleDescriptionChange}
              onKeyDown={getHotkeyHandler([
                [
                  'mod+Enter',
                  () => {
                    if (isAnyLoading || !state.description) return;
                    handleStartTaskCreation();
                  },
                ],
              ])}
              placeholder="Describe your task..."
              ref={textareaRef}
              value={state.description ?? ''}
            />
            {state.attachments.length ? (
              <XStack flexWrap="wrap" gap="sm">
                <FilePreviewsList files={state.attachments} maxItemWidth={200} onRemove={handleFileRemove} />
              </XStack>
            ) : null}
            {state.isScopeCheckLoading ? (
              <YStack gapY="md">
                <XStack alignItems="center" gap="sm">
                  <Spinner color="neutral.9" size={14} />
                  <span className={safeCss('text-textTertiary text-p-sm')}>Analyzing task</span>
                </XStack>
                {showScopeCheckStuckButton ? (
                  <XStack justifyContent="start">
                    <Button
                      className="border-2 border-borderPrimary"
                      isLoading={isRetrieveDraftTaskLoading}
                      onClick={manualRefetchScopeCheck}
                      size="sm"
                      variant="secondary"
                    >
                      Stuck loading? Click here.
                    </Button>
                  </XStack>
                ) : null}
              </YStack>
            ) : null}
            {state.scopeFailureReason ? (
              <InlineAlert description={getScopeFailureExplanationCopy(state.scopeFailureReason)} variant="error" />
            ) : null}
          </YStack>
          <XStack
            alignItems="center"
            className={concatClasses('gap-md', applyClass(isMultiLine, 'self-end'))}
            onClick={(e) => e.stopPropagation()}
          >
            <FileInput
              allowedFileTypes={['image', 'pdf']}
              buttonClassName="flex items-center justify-center size-[42px] rounded-full bg-cream-100/60 disabled:opacity-40 transition-transform active:scale-95"
              countMaxFiles={FILE_INPUT_COUNT_MAX_FILES}
              disabled={isAnyLoading}
              iconName="IconPaperclip"
              iconSize={17}
              noLabel
              onChange={(val) => handleFileChoose(val as DraftTaskAttachmentList)}
              value={state.attachments}
            />
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <Divider className="h-[42px]" orientation="vertical" />
            <button
              className={safeCss(
                'flex items-center justify-center h-[42px] w-[42px] rounded-[12px] transition-transform active:scale-95 gradient-border-button gradient-border-button-dark disabled:opacity-40',
              )}
              disabled={isContinueDisabled}
              type="submit"
            >
              {isAnyLoading ? (
                <Spinner color="neutral.1" size={16} />
              ) : (
                <Icon color="white" name="IconArrowRight" size={18} />
              )}
            </button>
          </XStack>
        </XStack>
        </form>
        <DragAndDropModal
          fileLimitReached={state.attachments?.length >= FILE_INPUT_COUNT_MAX_FILES}
          onFilesDropped={(attachments) => handleFileChoose(attachments as DraftTaskAttachmentList)}
        />
      </GradientBorderCard>
    </YStack>
  );
};

export const HomeCreateTaskInput = () => {
  return (
    <CreateDraftTaskProvider useLazyRetrieveDraftTaskQuery={useLazyRetrieveDraftTaskQuery}>
      <HomeCreateTaskInputInner />
    </CreateDraftTaskProvider>
  );
};
